import { inject, Injectable } from "@angular/core";
import { toSignal } from "@angular/core/rxjs-interop";
import { environment } from "@environments/environment";
import { Binder, BinderDetails, CardBinder, Page, Scan, Share, TopK, UserBinder } from "@models/binder.model";
import { Api } from "@services/api.service";
import { map, of, share, shareReplay, tap } from "rxjs";
import { Router } from "./route.service";
import { User } from "./user.service";
import { Card, CardSet } from "@models/card.model";

@Injectable({
	providedIn: 'root'
})
export class BinderService{
	private readonly api=inject(Api);
	private readonly binders$=this.getBinders$();
	private readonly binders=toSignal(this.binders$, {initialValue: null});
	private readonly router=inject(Router);
	private readonly user=inject(User);

	getBinders(){
		if(this.binders())
			return of(this.binders())
		else
			return this.binders$;
	}

	private getBinders$(){
		return this.api.get<Binder[]>(`/binders`)
			.pipe(
				shareReplay(1)
			);
	}

	getUserBinders(){
		return this.api.get<UserBinder[]>(`/my-binders`)
			.pipe(
				map(binders => {
					binders.sort((a: UserBinder, b: UserBinder) => b.openedAt.localeCompare(a.openedAt));
					return binders;
				})
			);
	}

	//retourne un UserBinder
	getUserBinder(ident: string){
		return this.getUserBinders()
			.pipe(
				map(uBinders => uBinders.find((u: any) => u.ident==ident))
			);
	}

	//retourne tous les éléments du binder : 
	// => UserBinder
	// => pages
	// => carte
	// => référence
	// => scans
	getDetailsUserBinder(binder: string){
		return this.api.get<BinderDetails>(`/my-binders/${binder}`);
	}

	//retourne tous les éléments du binder : 
	// => UserBinder
	// => pages
	// => carte
	// => référence
	// => scans
	getDetailsSharedBinder(binder: string, share: string){
		return this.api.get<BinderDetails>(`/binders/shared/${binder}`, {share});
	}

	//retourne tous les éléments pour la page 
	getDetailPage(ident: string, share: string){
		return this.api.get<PageDetailApiResponse>(`/binders/shared/pages/${ident}`, {share});
	}

	//retourne la liste des cartes que pocede l'utilisateur pour un set spécifique
	getCardStats(cardSet: string){
		return this.api.get<CardBinder[]>(`/my-stats/card-sets/${cardSet}`);
	}

	getBinder(ident: string){
		return this.getBinders()
			.pipe(
				map(binders => binders.find((b: any) => b.ident==ident))
			);
	}

	getImg(img: string){
	    if(img)
	      return `${environment.imgUrl}/binders/${img}`;
	    else
	      return '/img/skin/unknow-binder.svg';
	}

	getImgPage(page: Page, scan: Scan){
		let url: string;

		//Rappel : quand on vient d'ajouter une page, il n'y a pas de scan et l'image est dans scans 
		if(page.state=='empty')
			url=`${environment.imgUrl}/results/fail-scans/${scan.img}`;
		else if(scan && scan.state=='finished')
			url=`${environment.imgUrl}/results/pages/${page.img}`;
		else if(scan)
			url=`${environment.imgUrl}/scans/${scan.img}`;
		else
			url=`${environment.imgUrl}/scans/${page.img}`;
		return url;
	}

	getImgPageFinish(page: Page){
		return `${environment.imgUrl}/results/pages/${page.img}`;
	}

	getImgCard(card: CardBinder, scan?: Scan){
		let url: string;

		//Rappel : quand on vient d'ajouter une carte, il n'y a pas de scan et l'image est dans scans 
		if(card.state=='empty')
			url=`${environment.imgUrl}/results/fail-scans/${scan.img}`;
		else if(!card.scan || (scan && scan.state=='finished'))
			url=`${environment.imgUrl}/results/cards_rectified/${card.img}`;
		else if(scan)
			url=`${environment.imgUrl}/scans/${scan.img}`;
		else
			url=`${environment.imgUrl}/scans/${card.img}`;
		return url;
	}

	//on est sur que le scan est fini = cardBinder.img
	getImgInventory(img: string){
		return `${environment.imgUrl}/results/cards_rectified/${img}`;
	}

	setBinder(binder: Binder, img: File=null){
		let datas=JSON.parse(JSON.stringify(binder));
		datas.img=img;

		return this.api.post<Binder>(`/binders${((binder.ident)? `/${binder.ident}` : '')}`, Api.toFormData(datas))
			.pipe(
				tap(b => {
					if(this.binders() && binder.ident)
						this.binders().splice(this.binders().findIndex((e: any) => e.ident==binder.ident), 1, b);
					else
						this.binders().push(b);
				})
			);
	}

	getShareBinderData(uBinder: string, share: string){
		let data: ShareData={
		  title: `Legend-Binder`,
		  text: `${this.user.user().name} souhaite partager sa collection avec vous. Cliquez sur le lien pour consulter son binder.`,
		  url: window.location.origin+this.router.parse('share-binder', [uBinder])
		};

		this.addShareParam(data, share);
		return data;
	}

	getShareCardData(card: string, share: string){
		let data: ShareData={
		  title: `Legend-Binder`,
		  text: `${this.user.user().name} souhaite partager sa collection avec vous. Cliquez sur le lien pour consulter sa carte.`,
		  url: window.location.origin+this.router.parse('share-card', [card])
		};

		this.addShareParam(data, share);
		return data;
	}

	getSharePageData(page: string, share: string){
	    let data: ShareData={
	      title: `Legend-Binder`,
	      text: `${this.user.user().name} souhaite partager sa collection avec vous. Cliquez sur le lien pour consulter une page de son binder.`,
	      url: window.location.origin+this.router.parse('share-page', [page])
	    };

	    this.addShareParam(data, share);
	    return data;
	}

	addShareParam(data: ShareData, share: string){
      const url=new URL(data.url);
      url.searchParams.set('share', share);
      data.url=url.toString();
	}

	rmBinder(ident: string){
		return this.api.delete(`/binders/${ident}`)
			.pipe(
				tap(b => {
					if(this.binders())
						this.binders().splice(this.binders().findIndex((e: any) => e.ident==ident), 1);
				})
			);
	}

	bucket(binder: string, qte: number, showCode: boolean, name: string='binders.zip'){
		return this.api.getFile(`/binders/buckets/${binder}`, {qte, showCode})
			.pipe(
				tap(r => this.api.download(r, name))
			);
	}

	openCustomBinder(){
		return this.api.get<UserBinder>('/binders/customs/open');
	}

	openBinder(ident: string){
		return this.api.get<UserBinder>(`/binders/open/${ident}`);
	}

	rmUserBinder(uBinder: string){
		return this.api.delete<UserBinder>(`/my-binders/${uBinder}`);
	}

	getCard(card: string, edit: boolean=false){
		return this.api.get<CardDetailApiResponse | CardEditApiResponse>(`/my-binders/cards/${card}`, {edit});
	}

	getSharedCard(card: string, share: string){
		return this .api.get<CardDetailApiResponse>(`/binders/shared/cards/${card}`, {share});
	}

	rmCard(card: string){
		return this.api.delete<CardBinder>(`/cards/${card}`);
	}

	updateCard(card: CardBinder){
		return this.api.post<CardEditApiResponse>(`/my-binders/cards/${card.ident}`, card);
	}

	getMyCards(start: number, length: number){
		return this.api.get<MyCardApiResponse>(`/my-cards/cards`, {start, length});
	}

	getFavorites(){
		return this.api.get<FavoriteApiResponse>(`/my-favorites`);
	}

	toggleFavoriteBinder(uBinder: string){
		return this.api.get<UserBinder>(`/my-favorites/binders/${uBinder}`);
	}

	toggleFavoritePage(uBinder: string){
		return this.api.get<Page>(`/my-favorites/pages/${uBinder}`);
	}

	toggleFavoriteCard(uBinder: string){
		return this.api.get<CardBinder>(`/my-favorites/cards/${uBinder}`);
	}

	shareBinder(ident: string){
		return this.api.get<Share>(`/shares/binder/${ident}`);
	}

	sharePage(ident: string){
		return this.api.get<Share>(`/shares/page/${ident}`);
	}

	shareCard(ident: string){
		return this.api.get<Share>(`/shares/card/${ident}`);
	}

	disableShare(ident: string){
		return this.api.delete<Share>(`/shares/${ident}`);
	}

	getSharings(start: number, length: number){
		return this.api.get<Share[]>(`/my-shares`, {start, length});
	}
}

export type CardDetailApiResponse={
	cardBinder: CardBinder;
	cardIndex: Card;
	cardSet: CardSet;
	inventory: CardBinder[];
}

export type CardEditApiResponse={
	cardBinder: CardBinder;
	cardIndex: Card;
	topk: TopK[];
}

export type PageDetailApiResponse={
	page: Page;
	scan: Scan;
}

export type MyCardApiResponse={
	cardBinder: CardBinder[];
	references: Card[];
	scans: Scan[];
}

export type FavoriteApiResponse={
	binders: UserBinder[];
	pages: Page[];
	cardBinder: CardBinder[];
	references: Card[];
	scans: Scan[];
}